import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Button, Col, Empty, Modal, Row, Select, Space, Spin, Typography } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import TracPatchOverrideForm from '../schedule/tracpatch.override.form'
import ExerciseInstructionsModal from '../exercise/exercise.instructions.modal'
import serviceInterface from '../../services/service.interface'
import {
  fetchClinicSetting,
  saveClinicSetting,
  resetClinicSetting,
} from '../../store/action/settings.action'
import { repsByWeekFromRows, stripTracpatchReps } from './careplan.rows'

const SETTING_KEY = 'tracpatch-exercises'

// Clinic-scoped editor for TracPatch exercise overrides.
// Wraps the per-exercise TracPatchOverrideForm; saves all per-exercise
// overrides under a single ClinicSettings row keyed by 'tracpatch-exercises'.
function TracpatchExerciseSettingsForm(props) {
  // customerid (clinic page) or companyid (company page: what a new clinic
  // starts from) — the settings actions route on whichever is present.
  const { customerid, companyid } = props
  const scopeId = customerid || companyid
  // Copy differs by scope: a clinic edit reaches its patients; a company edit
  // reaches the clinics created after it.
  const who = customerid ? 'Patients in this clinic' : 'Clinics created from now on'
  const level = customerid ? 'clinic-level' : 'company-level'
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [exercises, setExercises] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  // clinicOverrideMap: { [exerciseId]: <override-fields-object> }
  const [clinicOverrideMap, setClinicOverrideMap] = useState({})
  // The care plan's exercise rows ({ exerciseid, startdate, enddate, repeat })
  // across this scope's surgery types. Reps come FROM the care plan
  // (2026-10-16); the Reps column below shows them read-only per week.
  const [carePlanRows, setCarePlanRows] = useState([])
  // Read-only patient-facing instructions preview for the selected exercise.
  const [viewInstructions, setViewInstructions] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const [exerciseRes, clinicRes] = await Promise.all([
          serviceInterface.ExerciseService().getAllExercises(),
          props.fetchClinicSetting({ customerid, companyid, key: SETTING_KEY }),
        ])
        if (cancelled) return
        const all = (exerciseRes && exerciseRes.exercises) || []
        const tracpatch = (Array.isArray(all) ? all : []).filter(
          (e) => e && e.type === 'TracPatch' && e.exerciseId != null
        )
        setExercises(tracpatch)
        if (tracpatch.length && selectedId == null) {
          setSelectedId(tracpatch[0].exerciseId)
        }
        const clinicValue =
          (clinicRes && clinicRes.data && clinicRes.data.value) || null
        setClinicOverrideMap((clinicValue && clinicValue.exercises) || {})
        // Care plan rows for every surgery type of this scope (knee first in
        // practice, the only type with TracPatch exercises). Best-effort: a
        // failure leaves the Reps column on dashes, it never blocks the panel.
        try {
          const dcp = serviceInterface.DefaultCarePlanService()
          const types = await dcp.getSurgeryTypes({ customerid, companyid })
          const plans = await Promise.all(
            (types || []).map((t) =>
              dcp.getDefaultCarePlan({ customerid, companyid, surgeryType: t.name })
            )
          )
          if (cancelled) return
          const rows = []
          plans.forEach((p) => ((p && p.exercises) || []).forEach((e) => rows.push(e)))
          setCarePlanRows(rows)
        } catch (e) {
          if (!cancelled) setCarePlanRows([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (scopeId) load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId])

  const selectedExercise = useMemo(
    () => exercises.find((e) => e.exerciseId === selectedId) || null,
    [exercises, selectedId]
  )

  const overridesForSelected =
    (selectedId != null && clinicOverrideMap[selectedId]) || {}

  // Per-week reps from the care plan rows of the selected exercise, matched by
  // the exercise document id. Shown read-only in the form's Reps column.
  const repsByWeek = useMemo(() => {
    if (!selectedExercise) return null
    const mine = carePlanRows.filter(
      (r) => r && String(r.exerciseid) === String(selectedExercise._id)
    )
    return repsByWeekFromRows(mine)
  }, [carePlanRows, selectedExercise])
  const repsSource = customerid ? "this clinic's care plan" : 'the company care plan'

  const handleOverrideChange = (nextOverrides) => {
    if (selectedId == null) return
    const next = { ...clinicOverrideMap }
    if (!nextOverrides || Object.keys(nextOverrides).length === 0) {
      delete next[selectedId]
    } else {
      next[selectedId] = nextOverrides
    }
    setClinicOverrideMap(next)
  }

  // Sets (requiredSets) is a valid tracpatchConfig field, so the clinic stores
  // it like any other override. Editing it merges requiredSets into the
  // selected exercise's clinic override (clearing removes it → falls back to
  // master / the 3-set default).
  const handleSetsChange = (val) => {
    if (selectedId == null) return
    const cur = { ...(clinicOverrideMap[selectedId] || {}) }
    if (val === undefined || val === null || val === '') {
      delete cur.requiredSets
    } else {
      cur.requiredSets = val
    }
    handleOverrideChange(cur)
  }

  // Default Sets to 3 when neither the clinic override nor the master config
  // defines it, so the field shows a sensible editable default.
  const DEFAULT_SETS = 3
  const setsValue =
    overridesForSelected.requiredSets != null
      ? overridesForSelected.requiredSets
      : selectedExercise &&
        selectedExercise.tracpatchConfig &&
        selectedExercise.tracpatchConfig.requiredSets != null
      ? selectedExercise.tracpatchConfig.requiredSets
      : DEFAULT_SETS

  const handleSave = async () => {
    setSaving(true)
    try {
      // Reps live in the care plan; any rep value still in the stored
      // overrides (requiredReps, per-week reps) is dropped so the apps'
      // fallback chain can never pick it up.
      const value = { exercises: stripTracpatchReps(clinicOverrideMap) }
      const res = await props.saveClinicSetting({
        customerid,
        companyid,
        key: SETTING_KEY,
        value,
      })
      if (res && !res.error) {
        setClinicOverrideMap(value.exercises)
        if (props.onSaved) props.onSaved()
      }
    } finally {
      setSaving(false)
    }
  }

  const handleResetExercise = () => {
    if (selectedId == null) return
    Modal.confirm({
      title: 'Reset this exercise to global defaults?',
      content:
        `This removes the ${level} override for this exercise. ${who} will see global defaults until the override is set again.`,
      okText: 'Reset',
      okButtonProps: { danger: true },
      onOk: async () => {
        const next = stripTracpatchReps({ ...clinicOverrideMap })
        delete next[selectedId]
        setClinicOverrideMap(next)
        setSaving(true)
        try {
          await props.saveClinicSetting({
            customerid,
            companyid,
            key: SETTING_KEY,
            value: { exercises: next },
          })
        } finally {
          setSaving(false)
        }
      },
    })
  }

  const handleResetAll = () => {
    Modal.confirm({
      title: 'Reset ALL TracPatch exercises to global defaults?',
      content:
        `This removes the ${level} override row entirely. ${who} will fall back to global defaults for every TracPatch exercise.`,
      okText: 'Reset all',
      okButtonProps: { danger: true },
      onOk: async () => {
        setSaving(true)
        try {
          await props.resetClinicSetting({
            customerid,
            companyid,
            key: SETTING_KEY,
          })
          setClinicOverrideMap({})
        } finally {
          setSaving(false)
        }
      },
    })
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin />
      </div>
    )
  }

  if (!exercises.length) {
    return (
      <Empty
        description='No TracPatch exercises found.'
        style={{ padding: 40 }}
      />
    )
  }

  const hasOverride = selectedId != null && clinicOverrideMap[selectedId]

  return (
    <div>
      <Row gutter={16} align='middle' style={{ marginBottom: 16 }}>
        <Col flex='auto'>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
            Exercise
          </div>
          <Select
            value={selectedId}
            onChange={(v) => setSelectedId(v)}
            style={{ width: 320 }}
          >
            {exercises.map((ex) => (
              <Select.Option key={ex.exerciseId} value={ex.exerciseId}>
                {ex.name}
                {clinicOverrideMap[ex.exerciseId] ? ' •' : ''}
              </Select.Option>
            ))}
          </Select>
          {selectedExercise && (
            <div style={{ marginTop: 6 }}>
              <Typography.Link onClick={() => setViewInstructions(true)}>
                <EyeOutlined style={{ marginRight: 6 }} />
                View instructions
              </Typography.Link>
            </div>
          )}
        </Col>
        <Col>
          <Space>
            <Button
              onClick={handleResetExercise}
              disabled={!hasOverride || saving}
            >
              Reset this exercise
            </Button>
            <Button onClick={handleResetAll} disabled={saving}>
              Reset all
            </Button>
            {props.onCancel && (
              <Button onClick={props.onCancel} disabled={saving}>
                Cancel
              </Button>
            )}
            <Button type='primary' onClick={handleSave} loading={saving}>
              Save
            </Button>
          </Space>
        </Col>
      </Row>

      {selectedExercise && (
        <>
          <Typography.Text
            type='secondary'
            style={{ fontSize: 12, display: 'block', marginBottom: 8 }}
          >
            Reps per week come from {repsSource}: this exercise&apos;s date
            windows and their Reps. Change them in the care plan panel; the
            Reps column below shows them read-only.
          </Typography.Text>
          <TracPatchOverrideForm
            masterConfig={selectedExercise.tracpatchConfig || {}}
            overrides={overridesForSelected}
            onChange={handleOverrideChange}
            exerciseName={selectedExercise.name}
            edit={true}
            numWeeks={14}
            sets={setsValue}
            onSetsChange={handleSetsChange}
            repsByWeek={repsByWeek}
            repsSource={repsSource}
          />
        </>
      )}

      <ExerciseInstructionsModal
        visible={viewInstructions}
        exercise={selectedExercise}
        onClose={() => setViewInstructions(false)}
      />
    </div>
  )
}

TracpatchExerciseSettingsForm.propTypes = {
  customerid: PropTypes.string,
  companyid: PropTypes.string,
  onSaved: PropTypes.func,
  onCancel: PropTypes.func,
}

const mapDispatchToProps = (dispatch) => ({
  fetchClinicSetting: (params) => dispatch(fetchClinicSetting(params)),
  saveClinicSetting: (params) => dispatch(saveClinicSetting(params)),
  resetClinicSetting: (params) => dispatch(resetClinicSetting(params)),
})

export default connect(null, mapDispatchToProps)(TracpatchExerciseSettingsForm)
